import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';

/**
 * Минимальный контракт формы (react-final-form FormApi), нужный для проверки.
 * Позволяет передавать и formRef.current КНМ, и decisionFormRef.current без конфликта дженериков.
 */
interface IValidatableForm {
  getState: () => { errors?: Record<string, unknown> };
  getRegisteredFields: () => string[];
  blur: (name: string) => void;
}

export type AdminSubjectsObjectsVariant = KnmSystemTypesEnum | 'DECISION';

interface IVariantConfig {
  /** Ключи ошибок ОБК/КЛ верхнего уровня для данного форка. */
  keys: string[];
  /** Из вложенного объекта-ключа исключить под-ключи (результаты/акт — их админ не правит). */
  excludeSubKeys?: Record<string, string[]>;
}

// ОБК/КЛ хранятся по-разному: у ЕРКНМ/решения — вложенные ключи, у ЕРП/ПМ — плоские поля субъекта.
const CONFIG: Record<AdminSubjectsObjectsVariant, IVariantConfig> = {
  [KnmSystemTypesEnum.ERKNM]: { keys: ['objectsErknm', 'organizations'] },
  [KnmSystemTypesEnum.ERP]: {
    keys: ['objects', 'objectsErvk', 'organizationName', 'inn', 'ogrn', 'subjectType', 'subjectTypeErvk'],
  },
  [KnmSystemTypesEnum.PM]: {
    keys: [
      'objectsPm',
      'subjectTypePm',
      'isOrganizationNew',
      'organizationName',
      'inn',
      'ogrn',
      'snils',
      'document',
      'country',
    ],
  },
  DECISION: {
    keys: ['objects', 'organization'],
    excludeSubKeys: { organization: ['act', 'resultDecisions'] },
  },
};

/** Есть ли хоть один реальный лист-ошибка в поддереве (с учётом массивов/undefined от RFF). */
const hasErrorsDeep = (node: unknown): boolean => {
  if (!node) {
    return false;
  }
  if (typeof node === 'string') {
    return node.trim().length > 0;
  }
  if (Array.isArray(node)) {
    return node.some(hasErrorsDeep);
  }
  if (typeof node === 'object') {
    return Object.values(node as Record<string, unknown>).some(hasErrorsDeep);
  }

  return false;
};

const stripExcluded = (value: unknown, exclude?: string[]): unknown => {
  if (!exclude || !value || typeof value !== 'object' || Array.isArray(value)) {
    return value;
  }
  const copy = { ...(value as Record<string, unknown>) };

  exclude.forEach(key => delete copy[key]);

  return copy;
};

export const validateAdminSubjectsObjects = (form: IValidatableForm, variant: AdminSubjectsObjectsVariant): boolean => {
  const config = CONFIG[variant];

  if (!config) {
    return false;
  }

  const { errors } = form.getState();

  const hasErrors = config.keys.some(key => hasErrorsDeep(stripExcluded(errors?.[key], config.excludeSubKeys?.[key])));

  if (!hasErrors) {
    return false;
  }

  form.getRegisteredFields().forEach(name => {
    const matchedKey = config.keys.find(
      key => name === key || name.startsWith(`${key}[`) || name.startsWith(`${key}.`),
    );

    if (!matchedKey) {
      return;
    }

    const excluded = config.excludeSubKeys?.[matchedKey];

    if (excluded?.some(sub => name.startsWith(`${matchedKey}.${sub}`))) {
      return;
    }
    form.blur(name);
  });

  return true;
};
